import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getAccessToken, clearTokens, subscribe } from '@/lib/tokenStore';
import { redirectToLogin, IDENTITY_URL } from '@/lib/identityApi';

const API = process.env.REACT_APP_BACKEND_URL || '';
const ROLE_LEVELS = { admin: 4, manager: 3, bedrijfsleider: 2, medewerker: 1 };

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(getAccessToken());
  const [loading, setLoading] = useState(true);

  // Sync local state when tokenStore changes (login/refresh/logout)
  useEffect(() => subscribe(() => setTokenState(getAccessToken())), []);

  const fetchMe = useCallback(async () => {
    const t = getAccessToken();
    if (!t) { setUser(null); return; }
    try {
      const res = await fetch(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!res.ok) {
        clearTokens();
        setUser(null);
        return;
      }
      const data = await res.clone().json();
      setUser(data.user || data);
    } catch {
      clearTokens();
      setUser(null);
    }
  }, []);

  // Re-fetch /me whenever token changes (mount, login, refresh, logout).
  // Critical: AuthCallback only calls setTokens(); this effect handles the
  // /me-fetch + user-state population. Single source of truth for re-renders.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await fetchMe(); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token, fetchMe]);

  const login = useCallback(() => {
    redirectToLogin();
  }, []);

  const logout = useCallback(async () => {
    // Defense-in-depth: zet sessionStorage flag zodat /login een schone
    // "Je bent uitgelogd" UI toont, ook als Identity's GET endpoint ooit
    // faalt (fallback in pages/Login.jsx).
    try {
      sessionStorage.setItem('tw1_just_logged_out', String(Date.now()));
    } catch { /* privacy mode — negeer */ }

    // 1) Wis lokale in-memory tokens EERST — mocht de redirect falen, dan
    //    is de user UI-side al uitgelogd.
    clearTokens();
    setUser(null);

    // 2) Best-effort: flush onze backend's 60s validate-cache. Geen
    //    Authorization header (token is net gewist) — backend doet dan een
    //    no-op, maar acceptable; de echte cache-invalidatie volgt via TTL
    //    of via Identity's session kill in stap 3.
    try {
      await fetch(`${API}/api/auth/logout`, { method: 'POST' });
    } catch { /* netwerk faal — silent */ }

    // 3) Top-level GET redirect naar Identity's logout endpoint:
    //    - Identity clear `tw1_id_session` via Set-Cookie: Max-Age=0
    //    - Identity 302-redirect naar `returnTo` → onze /login?logout=1
    //    Top-level GET stuurt SameSite=Lax cookies wel mee — geen CORS.
    //
    //    BELANGRIJK: Identity's whitelist staat ALLEEN productie origins
    //    (truckwash-ops.nl) toe als `returnTo`. Preview/dev origins krijgen
    //    een 400 — dan vallen we terug op een lokale redirect zodat de UI
    //    in beide omgevingen consistent werkt.
    const origin = window.location.origin;
    const isWhitelistedOrigin = /^https:\/\/truckwash-ops\.nl$/i.test(origin);
    if (isWhitelistedOrigin) {
      const returnTo = `${origin}/login?logout=1`;
      window.location.href = `${IDENTITY_URL}/api/auth/logout?returnTo=${encodeURIComponent(returnTo)}`;
    } else {
      // Preview/dev: skip Identity GET (zou 400 geven). Lokale fallback —
      // sessie cookie blijft leven tot natuurlijke expiry, maar UI is clean.
      window.location.href = '/login?logout=1';
    }
  }, []);

  // ── Role helpers ────────────────────────────────────────────────────────
  const getRoleLevel = (rol) => ROLE_LEVELS[rol] || 0;
  const isAdmin = () => getRoleLevel(user?.rol) >= 4;
  const isManager = () => getRoleLevel(user?.rol) >= 3;
  const isBedrijfsleider = () => getRoleLevel(user?.rol) >= 2;
  const canManage = () => getRoleLevel(user?.rol) >= 3;

  const canEdit = (locatieId) => {
    if (!user) return false;
    const level = getRoleLevel(user.rol);
    if (level >= 4) return true;
    if (level >= 3) {
      if (!user.locaties || user.locaties.length === 0) return true;
      return user.locaties.includes(locatieId);
    }
    if (level >= 2) return user.locaties?.includes(locatieId);
    return false;
  };

  const hasLocationAccess = (locatieId) => {
    if (!user) return false;
    const level = getRoleLevel(user.rol);
    if (level >= 4) return true;
    if (level >= 3 && (!user.locaties || user.locaties.length === 0)) return true;
    return user.locaties?.includes(locatieId);
  };

  const canManageUser = (targetRol) => {
    if (!user) return false;
    const actorLevel = getRoleLevel(user.rol);
    const targetLevel = getRoleLevel(targetRol);
    if (targetLevel >= 4 && actorLevel < 4) return false;
    if (actorLevel >= 4) return true;
    if (actorLevel >= 3) return targetLevel < 4;
    return false;
  };

  const maxAssignableRole = () => {
    const level = getRoleLevel(user?.rol);
    if (level >= 4) return 'admin';
    if (level >= 3) return 'manager';
    return null;
  };

  // ── Page/tool access — Identity Platform is auth-only sinds v18.1, dus
  // alle pagina-zichtbaarheid wordt lokaal gecontroleerd via rol-helpers.
  const hasPageAccess = () => true;
  const hasToolAccess = () => !!user;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      initialized: !loading,
      login,
      logout,
      isAuthenticated: !!user,
      isAdmin,
      isManager,
      isBedrijfsleider,
      canManage,
      canEdit,
      hasLocationAccess,
      canManageUser,
      maxAssignableRole,
      getRoleLevel,
      hasPageAccess,
      hasToolAccess,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
